import { useEffect, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "../../../lib/motion.js";
import { hasMoreBelow } from "./scroll-more.js";

function useMoreBelow(node: HTMLElement | null): boolean {
  const [more, setMore] = useState(false);

  useEffect(() => {
    if (!node) return;
    const update = () => setMore(hasMoreBelow(node));
    update();
    node.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(node);
    if (node.firstElementChild) observer?.observe(node.firstElementChild);
    return () => {
      node.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      observer?.disconnect();
    };
  }, [node]);

  return more;
}

/**
 * Organization list inside the switcher popover: scrolls on its own with the
 * scrollbar hidden, and shows a decorative chevron while rows remain below.
 */
export function SwitcherScrollArea({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): React.JSX.Element {
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const more = useMoreBelow(node);
  const reduceMotion = useReducedMotion() ?? false;

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={setNode}
        role="group"
        aria-label={label}
        className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-testid="organization-switcher-scroll"
      >
        <div>{children}</div>
      </div>
      <AnimatePresence initial={false}>
        {more ? (
          <motion.div
            key="more"
            className="pointer-events-none absolute inset-x-0 bottom-0 flex h-9 items-end justify-center rounded-b-[10px] bg-[linear-gradient(to_top,#ffffff_30%,rgba(255,255,255,0))] pb-1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.18, ease: "easeOut" }}
            aria-hidden="true"
            data-testid="organization-switcher-scroll-hint"
          >
            <motion.span
              className="inline-grid size-5 place-items-center rounded-full border border-[#cfe8dd] bg-brand-mint text-brand-primary shadow-[0_2px_8px_#1c50301f]"
              animate={reduceMotion ? { y: 0 } : { y: [0, 3, 0] }}
              transition={
                reduceMotion ? { duration: 0 } : { duration: 1.6, ease: "easeInOut", repeat: Infinity }
              }
            >
              <ChevronDown size={13} strokeWidth={2.4} aria-hidden="true" />
            </motion.span>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
